import { useRef, useState, useEffect, useCallback } from "react";
import classNames from "classnames";
import { Upload, UploadFile } from "antd";
import { UploadRef } from "antd/es/upload/Upload";
import { RcFile, UploadChangeParam } from "antd/es/upload";
import { useDragDrop } from "./useDragDrop";
import styles from "./style.module.scss";
import SupportedFormats from "./SupportedFormats";
import { useDispatch } from "react-redux";
import { setValidationLoading } from "redux/CreateDataset/actions";

const { Dragger } = Upload;

export type SupportedFormatType =
  | "Images"
  | "Annotations"
  | "Archives"
  | "DB"
  | "Classes"
  | "DB_Export";
export type SupportedFileFormat =
  | "JPG"
  | "PNG"
  | "JPEG"
  | "CSV"
  | "JSON"
  | "ZIP"
  | "TAR";

export interface Format {
  type: SupportedFormatType;
  formats: SupportedFileFormat[];
}

export interface FormatConfig {
  formatList: Format[];
  displayFormats: boolean;
}

export interface DragAndDropBaseProps {
  title: string;
  textButton: string;

  loading?: boolean;
  icon?: JSX.Element;
  formatConfig?: FormatConfig;
  directory?: boolean;
  tooltipText?: string | JSX.Element;

  onFilesChange: (files: File[]) => void;
}

const DragAndDropBase = ({
  icon,
  title,
  textButton,
  loading = false,
  directory = false,
  formatConfig = { formatList: [], displayFormats: true },
  tooltipText,
  onFilesChange,
}: DragAndDropBaseProps) => {
  const { formatList, displayFormats } = formatConfig;
  const timeoutRef = useRef<number | null>(null);
  const draggerRef = useRef<UploadRef>(null);
  const dispatch = useDispatch();
  const { handleDrop, isDragDropProcessing } = useDragDrop({
    onFilesChange,
    dispatch,
  });
  const [isDragOver, setIsDragOver] = useState(false);

  const contentClassnames = classNames(styles.uploadContent, {
    [styles.rowLayout]: !displayFormats,
    [styles.dragOver]: isDragOver,
  });

  const stopValidationLoading = useCallback(() => {
    dispatch(setValidationLoading(false));
  }, [dispatch]);

  const handleClick = () => {
    dispatch(setValidationLoading(true));
    // Set validation loading immediately when user clicks to open file picker
    if (draggerRef.current) {
      window.addEventListener("cancel", stopValidationLoading);
    }
  };

  const handleChange = (info: UploadChangeParam<UploadFile>) => {
    // Skip onChange if we're processing drag & drop
    if (isDragDropProcessing.current) {
      return;
    }

    // Antd calls onChange for every single file, so we need to debounce the calls
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = window.setTimeout(() => {
      // Extract actual File objects from Ant Design's UploadFile wrappers
      const files = info.fileList
        .map((uploadFile) => uploadFile.originFileObj)
        .filter((file): file is RcFile => file !== undefined);

      onFilesChange(files);
      window.removeEventListener("cancel", stopValidationLoading);
    }, 200); // wait for 200ms pause in events
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    // Only set drag over to false if we're leaving the entire dragger area
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDropWithState = (e: React.DragEvent<HTMLDivElement>) => {
    setIsDragOver(false);
    handleDrop(e);
  };

  useEffect(() => {
    return () => {
      window.removeEventListener("cancel", stopValidationLoading);
    };
  }, [stopValidationLoading]);

  return (
    <div
      className={styles.dragDropContainer}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onClick={handleClick}
    >
      <Dragger
        ref={draggerRef}
        name="files"
        fileList={[]} //always keep empty after upload
        multiple={true}
        directory={directory}
        showUploadList={false}
        className={styles.dragger}
        onChange={handleChange}
        onDrop={handleDropWithState}
        beforeUpload={() => false}
        disabled={loading}
      >
        <div className={contentClassnames}>
          {loading ? (
            <div className={styles.loadingContainer}>
              <div className={styles.spinner}></div>
            </div>
          ) : (
            <>
              {icon && <div className={styles.iconContainer}>{icon}</div>}
              <div className={styles.subContent}>
                <div className={styles.titleSection}>
                  <div className={styles.title}>{title}</div>
                  {<div className={styles.orText}>OR</div>}
                </div>
                <div className={styles.textButton}>{textButton}</div>
              </div>
              {formatList.length > 0 && displayFormats && (
                <SupportedFormats
                  formatList={formatList}
                  tooltipText={tooltipText}
                />
              )}
            </>
          )}
        </div>
      </Dragger>
    </div>
  );
};

export { useDragDrop } from "./useDragDrop";
export default DragAndDropBase;
